import { useReducer, useMemo, useEffect } from "react";
import { projectReducer, getObjectById } from "./projectReducer";
import { OBJECT_TYPES } from "../configuratorConstants";
import safeToast from '../utils/safeToast';

const LOCAL_STORAGE_KEY = "rebbud_project_autosave";
const EXPIRATION_TIME_MS = 30 * 1000;

const detectFramelessUnlock = (projectData) => {
  if (!projectData?.projectObjects) {
    return false;
  }

  for (const floorObjects of Object.values(projectData.projectObjects)) {
    if (!Array.isArray(floorObjects)) continue;
    const hasFrameless = floorObjects.some((obj) => {
      if (!obj) return false;
      if (
        obj.type === OBJECT_TYPES.FRAMELESS_PARTITION ||
        obj.type === OBJECT_TYPES.FRAMELESS_WALL
      ) {
        return true;
      }
      if (obj.type === OBJECT_TYPES.FRAMELESS_BUILDING) {
        const hasPerimeter =
          Array.isArray(obj.perimeterWalls) && obj.perimeterWalls.length > 0;
        const hasInternal =
          Array.isArray(obj.internalWalls) && obj.internalWalls.length > 0;
        return hasPerimeter || hasInternal;
      }
      return false;
    });
    if (hasFrameless) {
      return true;
    }
  }

  return false;
};

const getInitialState = (t) => {
  const initialFloorId = "floor_0";
  return {
    projectObjects: { [initialFloorId]: [] },
    floors: [
      {
        id: initialFloorId,
        name: t ? t("statusBar.floor") + " 1" : "Поверх 1",
      },
    ],
    currentFloorId: initialFloorId,
    selectedObjectId: null,
    activeMode: "modular",
    clickedJointSegmentIndex: null,
    framelessElementsUnlocked: false,
    notification: null,
  };
};

const loadStateFromLocalStorage = (t) => {
  try {
    const serializedState = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (serializedState === null) {
      return getInitialState(t);
    }

    const savedObject = JSON.parse(serializedState);

    if (savedObject && savedObject.timestamp && savedObject.projectData) {
      const timeElapsed = Date.now() - savedObject.timestamp;

      if (timeElapsed < EXPIRATION_TIME_MS) {
        if (savedObject.projectData.projectObjects) {
          for (const floorId in savedObject.projectData.projectObjects) {
            savedObject.projectData.projectObjects[floorId].forEach((obj) => {
              if (obj.type === "module" && obj.mirroredX !== undefined) {
                delete obj.mirroredX;
              }
            });
          }
        }
        if (
          savedObject.projectData.framelessElementsUnlocked === undefined
        ) {
          savedObject.projectData.framelessElementsUnlocked =
            detectFramelessUnlock(savedObject.projectData);
        }
        savedObject.projectData.notification = null;
        return savedObject.projectData;
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        console.log("Autosave data expired. Clearing state.");
      }
    }

    const initialState = getInitialState(t);
    return initialState;
  } catch (err) {
    console.error("Could not load state from localStorage", err);
    return getInitialState(t);
  }
};

export const useProjectState = (t) => {
  const [state, dispatch] = useReducer(projectReducer, undefined, () =>
    loadStateFromLocalStorage(t),
  );

  useEffect(() => {
    try {
      const dataToSave = {
        projectData: state,
        timestamp: Date.now(),
      };
      const cleanedState = JSON.parse(JSON.stringify(state)); 
      if (cleanedState.projectObjects) {
        for (const floorId in cleanedState.projectObjects) {
          cleanedState.projectObjects[floorId].forEach((obj) => {
            if (obj.type === "module" && obj.mirroredX !== undefined) {
              delete obj.mirroredX;
            }
          });
        }
      }
      delete cleanedState.notification;
      dataToSave.projectData = cleanedState;

      const serializedState = JSON.stringify(dataToSave);
      localStorage.setItem(LOCAL_STORAGE_KEY, serializedState);
    } catch (err) {
      console.error("Could not save state to localStorage", err);
    }
  }, [state]);

  useEffect(() => {
    if (state.notification) {
      const { type, message } = state.notification;
      safeToast[type](message);
      dispatch({ type: 'CLEAR_NOTIFICATION' });
    }
  }, [state.notification]);

  const objectsOnCurrentFloor = useMemo(
    () => state.projectObjects[state.currentFloorId] || [],
    [state.projectObjects, state.currentFloorId],
  );

  const primarySelectedObject = useMemo(
    () =>
      getObjectById(
        state.selectedObjectId,
        state.currentFloorId,
        state.projectObjects,
      ),
    [state.selectedObjectId, state.currentFloorId, state.projectObjects],
  );

  const currentFloorIndex = useMemo(
    () => state.floors.findIndex((f) => f.id === state.currentFloorId),
    [state.floors, state.currentFloorId],
  );

  return {
    state,
    dispatch,
    objectsOnCurrentFloor,
    primarySelectedObject,
    currentFloorIndex,
  };
};